import { US_STATES_AND_TERRITORIES } from '~/constants';
import { getEmployerFacilities } from '~/features/market/facility';
import { getEmployerFacilityDisplayName } from '~/features/market/facility/utils';
import { OptionType } from '~/features/types';
import { EmployerFacility } from '~/types/hlx/employerFacility';
import { JobListingFacility } from '~/types/hlx/jobListingFacility';
import { MsaasJobListingFacility } from '~/types/hlx/msaasJobListingFacility';
import { FacilityOption } from './types';

type ListingFacility = EmployerFacility | JobListingFacility | MsaasJobListingFacility;

// [MCV-1876] "port clinton" / "PORT CLINTON" / "winston-salem" -> proper per-word case.
const titleCaseCity = (city: string): string =>
  city
    .toLowerCase()
    .replace(/(^|[\s-])(\p{L})/gu, (_, lead, letter) => lead + letter.toUpperCase());

// [MCV-1546] The label already carries the facility's state, but only as text.
// Keeping the code as data is what lets the form autofill the states list from
// the selected facility instead of leaving it blank.
export const toFacilityOption = (facility: EmployerFacility): FacilityOption => {
  const address = facility.facility?.address;
  const rawCity = address?.city?.trim() ?? '';
  const city = titleCaseCity(rawCity);
  const location = [city, address?.stateCode].filter(Boolean).join(', ');
  // [MCV-1876] Names saved from the picker carry their own "(CITY, ST)" suffix;
  // the display name strips it so the location below is the only one (and falls
  // back when facility_name is NULL, which the column allows). A name that
  // already ends in ", City, ST" is left alone rather than getting the location
  // a second time.
  const name = getEmployerFacilityDisplayName(facility);
  const alreadyLocated =
    Boolean(location) && name.toLowerCase().endsWith(`, ${location}`.toLowerCase());
  return {
    label: location && !alreadyLocated ? `${name}, ${location}` : name,
    // EmployerFacility.id is non-nullable, and defending against it anyway made
    // things worse: `''` here becomes Number('') === 0 at submit, so a facility
    // id of 0 would be PUT. The address chain above genuinely is optional and
    // is guarded; the id is not.
    value: facility.id.toString(),
    ...(address?.stateCode ? { stateCode: address.stateCode } : {}),
  };
};

// [MCV-1575] Until the unique constraint lands everywhere, an employer can hold
// several rows for the same facility, which render as options the user cannot
// tell apart -- each with a different employerFacilityId, so whichever one they
// happen to click is arbitrary. Deduping on `facilityId` (the underlying
// facility) rather than the option `value` (the employer_facility row id) is
// what actually collapses them. The API orders is_primary DESC, id ASC, so the
// first occurrence is the same survivor the dedupe migration keeps.
export const dedupeByFacility = (facilities: EmployerFacility[]): EmployerFacility[] => {
  const seen = new Set<number>();
  return facilities.filter((facility) => {
    if (seen.has(facility.facilityId)) return false;
    seen.add(facility.facilityId);
    return true;
  });
};

export const loadEmployerFacilitiesOptions = async (): Promise<FacilityOption[]> => {
  const facilities = await getEmployerFacilities();
  return dedupeByFacility(facilities).map(toFacilityOption);
};

// [MCV-1546] Falls back to a code-only option for a stored code the client list
// does not carry (e.g. a legacy UM from before the list matched the API's
// STATE_CODES). An unmatched code still has to ride through: the states PUT is a
// full replace, so dropping it here would delete it from the listing.
export const facilityStateOptions = (facilities: FacilityOption[]): OptionType[] => {
  const codes = Array.from(
    new Set(facilities.map((facility) => facility.stateCode).filter(Boolean) as string[])
  );
  return codes.map(
    (code) =>
      US_STATES_AND_TERRITORIES.find((state) => state.value === code) ?? {
        value: code,
        label: code,
      }
  );
};

export const transformFacilitiesFormToApi = (formValues: number[]) => {
  return JSON.stringify({
    facilityIds: formValues,
  });
};

export const getEmployerFacilityId = (facility: ListingFacility): number => {
  if ('employerFacilityId' in facility) {
    return facility.employerFacilityId;
  }
  return facility.id;
};
